import BTbotbutton from '@common-components/bt-chatbot/bot-components/bt-bot-button';
import RowStack from '@common-components/common/row-stack/RowStack';
import { getNodeDataByNodeId } from '@common-reducers/ChatbotSelectors';
import { changeNodeDataThunk } from '@common-reducers/ChatbotThunks';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { TriggerSource, TriggerType, TriggerSourceData, getAvailableTriggerSources, UserRole, TRIGGER_SOURCE_KEY_MAP } from '@common/types/trigger.types';
import { selectEngineState } from '@common/components/connected-engines/connected-engines.selectors';
import { ContactPickerField, SelectedContact } from '@extension/context/content/components/shared';
import { waQueryContactsAndChatsTool } from '@common/tools/whatsapp';
import { Checkbox, Divider, Stack, Typography, Box } from '@mui/material';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classes from "./trigger-node.module.scss";
import { SaveButton } from '../../bot-components/save-button';
import { TriggerSourceConfig } from '@common/components/TriggerSourceConfig';
import { selectUserObject } from '@/common/reducers/UserSelectors';
import { namespace } from '@common/config/constants';
import { analyzeTriggerScopeFacts } from '@common/utils/trigger-scope-summary';

interface TriggerState {
    type: 'trigger';
    triggerType: TriggerType;
    contacts: string[]; // Array of contact IDs
    allowedTriggerSources?: TriggerSource[]; // Array of allowed trigger sources
    triggerSourceData?: Partial<Record<TriggerSource, TriggerSourceData>>; // Source-specific configuration data
}

interface Props {
    onClose: () => void;
    id?: string;
}

export default function AddTriggerDialog({ onClose, id }: Props) {
    const { t } = useTranslation(namespace);
    const dispatch = useAppDispatch();

    const data = useAppSelector(state => getNodeDataByNodeId(state, id ?? '')) as TriggerState | undefined;

    // Get user role directly from the state slice
    // Adjust 'UserReducer' and 'role' if your state structure is different
    const userRole = useAppSelector(state => selectUserObject(state)?.role);
    // #184 A3: offer the Server Gateway source when the active engine is a gateway.
    const hasGateway = useAppSelector(state => selectEngineState(state).hasGateway);

    // Get available trigger sources based on role (+ gateway availability)
    const availableTriggerSources = getAvailableTriggerSources(userRole ?? '', { hasGateway });

    // Convert legacy 'twilio webhook' to 'blueticks assistant' for backwards compatibility
    const normalizeSourcesForBackwardCompatibility = (sources?: TriggerSource[]): TriggerSource[] => {
        if (!sources?.length) {
            return [];
        }

        return sources.map(source =>
            source === 'twilio webhook' as unknown as TriggerSource ? 'blueticks assistant' : source
        );
    };

    // Convert a string with spaces to camelCase
    // e.g., 'instagram page' becomes 'instagramPage'
    const toCamelCase = (str: string): string => {
        return str.replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) => {
            return index === 0 ? word.toLowerCase() : word.toUpperCase();
        }).replace(/\s+/g, '');
    };

    const [localTriggerState, setLocalTriggerState] = useState<TriggerState>(() => {
        // If we have existing data, use it with normalized sources
        if (data) {
            const normalizedSources = normalizeSourcesForBackwardCompatibility(data?.allowedTriggerSources);
            
            // Handle triggerSourceData normalization
            const normalizedSourceData: Record<string, TriggerSourceData> = data.triggerSourceData
                ? { ...(data.triggerSourceData as Record<string, TriggerSourceData>) }
                : {};

            // Convert keys from multiple possible formats to camelCase
            // This handles backward compatibility for existing data
            const possibleKeys = ['instagram page', 'instagram'];
            possibleKeys.forEach(key => {
                if (normalizedSourceData[key] && !normalizedSourceData[toCamelCase(key)]) {
                    normalizedSourceData[toCamelCase(key)] = normalizedSourceData[key];
                    delete normalizedSourceData[key];
                }
            });
            
            return {
                ...data,
                allowedTriggerSources: normalizedSources,
                triggerSourceData: normalizedSourceData
            };
        }

        // For new trigger nodes, default to the first *available* trigger source for the user's role
        const firstAvailableSource = availableTriggerSources.length > 0 ? availableTriggerSources[0] : null;
        return {
            type: 'trigger',
            triggerType: 'blockList',
            contacts: [],
            allowedTriggerSources: firstAvailableSource ? [firstAvailableSource.value] : [],
            triggerSourceData: {}
        };
    });

    const handleTriggerTypeChange = useCallback((value: string) => {
        setLocalTriggerState(prev => ({
            ...prev,
            triggerType: value as TriggerType,
        }));
    }, []);

    const handleTriggerSourceChange = (source: TriggerSource) => {
        setLocalTriggerState(prev => {
            const currentSources = prev?.allowedTriggerSources || [];
            const newSources = currentSources.includes(source)
                ? currentSources.filter(s => s !== source) // Remove if already exists
                : [...currentSources, source]; // Add if doesn't exist

            return {
                ...prev,
                allowedTriggerSources: newSources
            };
        });
    };

    const handleUpdateSourceData = (source: TriggerSource, data: TriggerSourceData) => {
        setLocalTriggerState(prev => {
            // Get existing source data or initialize empty object
            const sourceData = prev.triggerSourceData || {};
            
            // Convert any source name to camelCase consistently
            const storageKey = toCamelCase(source);
            
            return {
                ...prev,
                triggerSourceData: {
                    ...sourceData,
                    [storageKey]: data
                }
            };
        });
    };

    const isTriggerSourceChecked = (source: TriggerSource): boolean => {
        return (localTriggerState?.allowedTriggerSources || []).includes(source);
    };

    // Mirror the ContactSelect pattern from /test-components: keep the rich
    // contact records (name + avatar) alongside the saved string ids, so chips
    // render with their real labels — same as the Scheduled Message Editor.
    const [selectedContacts, setSelectedContacts] = useState<SelectedContact[]>(
        () => (data?.contacts || []).map(id => ({ id })),
    );

    const handleContactSelected = useCallback((contact: SelectedContact) => {
        setSelectedContacts(prev => (prev.some(c => c.id === contact.id) ? prev : [...prev, contact]));
        setLocalTriggerState(prev => {
            const existing = prev.contacts || [];
            return existing.includes(contact.id)
                ? prev
                : { ...prev, contacts: [...existing, contact.id] };
        });
    }, []);

    const handleContactRemoved = useCallback((contactId: string) => {
        setSelectedContacts(prev => prev.filter(c => c.id !== contactId));
        setLocalTriggerState(prev => ({
            ...prev,
            contacts: (prev.contacts || []).filter(id => id !== contactId),
        }));
    }, []);

    const saveTriggerData = async () => {
        await dispatch(changeNodeDataThunk({
            id,
            dataToPatch: localTriggerState
        }));

        return true;
    };

    // Load contacts when component mounts
    useEffect(() => {
        waQueryContactsAndChatsTool({});
    }, [dispatch]);

    // This function remains necessary because the child calls the callback 
    // with a partial update, and we need to merge it with the previous state.
    const handleUpdateTriggerData = (partialData: Partial<TriggerState>) => {
        setLocalTriggerState(prev => ({
            ...prev,
            ...partialData,
            // Ensure nested objects like triggerSourceData are merged correctly if needed
            triggerSourceData: {
                ...prev.triggerSourceData,
                ...partialData.triggerSourceData,
            }
        }));
    };

    return (
        <>
            <RowStack className={classes.add_trigger_dialog_header}>
                <span style={{ color: 'var(--color-text, #111827)' }}>{t('chatbotNodes.triggerSettings')}</span>
            </RowStack>

            <Stack className={classes.add_trigger_dialog_body} sx={{ width: 580, maxWidth: '90vw' }}>
                {/* Source selection — subtitle only shown when there's an actual choice. */}
                {availableTriggerSources.length > 1 && (
                    <Typography variant="body2" sx={{ mb: 1.5, color: 'var(--color-text-muted)' }}>
                        {t('chatbot.trigger.selectSources')}
                    </Typography>
                )}
                <Stack spacing={1}>
                    {availableTriggerSources.map((source) => {
                        const IconComponent = source.icon;
                        const sourceKey = TRIGGER_SOURCE_KEY_MAP[source.value];
                        const sourceLabel = sourceKey
                            ? t(`chatbot.trigger.sources.${sourceKey}.label`, { defaultValue: source.label })
                            : source.label;
                        const sourceDescription = sourceKey
                            ? t(`chatbot.trigger.sources.${sourceKey}.description`, { defaultValue: source.description || '' })
                            : source.description;
                        const isChecked = isTriggerSourceChecked(source.value);
                        return (
                            <Box
                                key={source.value}
                                onClick={() => handleTriggerSourceChange(source.value)}
                                sx={{
                                    p: 1.5,
                                    borderRadius: '12px',
                                    cursor: 'pointer',
                                    border: '1px solid',
                                    borderColor: isChecked ? 'var(--color-primary)' : 'var(--color-border)',
                                    background: isChecked
                                        ? 'var(--color-primary-light, rgba(37, 211, 102, 0.08))'
                                        : 'var(--color-surface)',
                                    transition: 'border-color 0.15s ease, background 0.15s ease',
                                    '&:hover': {
                                        borderColor: 'var(--color-primary)',
                                    },
                                }}
                            >
                                <Stack direction="row" spacing={1.5} alignItems="center">
                                    <Checkbox
                                        checked={isChecked}
                                        onChange={() => { /* parent Box handles toggle */ }}
                                        color="primary"
                                        sx={{ p: 0.5, pointerEvents: 'none', '& .MuiSvgIcon-root': { fontSize: 20 } }}
                                    />
                                    <Box
                                        sx={{
                                            width: 40,
                                            height: 40,
                                            borderRadius: '10px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            background: 'rgba(37, 211, 102, 0.12)',
                                            color: '#25D366',
                                            flexShrink: 0,
                                        }}
                                    >
                                        <IconComponent style={{ fontSize: 22, width: 22, height: 22 }} />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="body2" fontWeight={600} sx={{ color: 'var(--color-text)' }}>
                                            {sourceLabel}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: 'var(--color-text-muted)', display: 'block', lineHeight: 1.4 }}>
                                            {sourceDescription}
                                        </Typography>
                                    </Box>
                                </Stack>

                                {isChecked && source.requiresConfiguration && (
                                    <Box sx={{ mt: 1.5, ml: 6 }} onClick={(e) => e.stopPropagation()}>
                                        <TriggerSourceConfig
                                            source={source}
                                            triggerData={localTriggerState}
                                            onUpdateTriggerData={handleUpdateTriggerData}
                                        />
                                    </Box>
                                )}
                            </Box>
                        );
                    })}
                    {availableTriggerSources.length === 0 && (
                        <Typography variant="caption" sx={{ color: 'var(--color-text-muted)', fontStyle: 'italic', mt: 1 }}>
                            {t('chatbot.trigger.noSourcesAvailableForRole')}
                        </Typography>
                    )}
                </Stack>

                <Divider sx={{ my: 2.5 }} />

                <div className={classes.section_title}>{t('chatbotNodes.contactListType')}</div>
                <RadioGroup
                    value={localTriggerState.triggerType}
                    onValueChange={handleTriggerTypeChange}
                    className="gap-2 mb-4"
                >
                    {([
                        { value: 'blockList', label: t('chatbot.trigger.blockListDescription') },
                        { value: 'allowList', label: t('chatbot.trigger.allowListDescription') },
                    ] as const).map(({ value, label }) => {
                        const checked = localTriggerState.triggerType === value;
                        return (
                            <Label
                                key={value}
                                htmlFor={`trigger-type-${value}`}
                                className={cn(
                                    'flex items-center gap-3 px-3 py-2.5 rounded-xl border cursor-pointer transition-colors',
                                    checked
                                        ? 'border-[var(--color-primary)] bg-[var(--color-primary-light,rgba(37,211,102,0.08))]'
                                        : 'border-[var(--color-border)] hover:border-[var(--color-text-muted)]',
                                )}
                            >
                                <RadioGroupItem value={value} id={`trigger-type-${value}`} />
                                <span className="text-sm" style={{ color: 'var(--color-text)' }}>{label}</span>
                            </Label>
                        );
                    })}
                </RadioGroup>

                <div className={classes.section_title}>{t('chatbotNodes.contactList')}</div>

                <Box className="nowheel" sx={{ mb: 2 }}>
                    <ContactPickerField
                        collapsible
                        value={localTriggerState.contacts || []}
                        selectedContacts={selectedContacts}
                        onContactSelected={handleContactSelected}
                        onContactRemoved={handleContactRemoved}
                        showRefreshButton
                        height={280}
                        searchPlaceholder={
                            (localTriggerState.contacts || []).length > 0
                                ? t('contacts.addMore', { defaultValue: 'Add more contacts...' })
                                : t('chatbot.enterNameOrNumber')
                        }
                    />
                </Box>

                {/* FE#180: live plain-language preview of who the agent will answer,
                    plus a guardrail when the scope reduces to "all direct messages"
                    (the credit-burn case) or looks inverted (only groups blocked). */}
                {(() => {
                    const scope = analyzeTriggerScopeFacts({
                        triggerType: localTriggerState.triggerType,
                        contacts: localTriggerState.contacts || [],
                    });
                    const summary =
                        scope.kind === 'everyone' ? t('chatbot.trigger.scopeEveryone')
                        : scope.kind === 'no_one' ? t('chatbot.trigger.scopeNoOne')
                        : scope.kind === 'only_listed' ? t('chatbot.trigger.scopeOnlyListed', { count: scope.contactCount })
                        : t('chatbot.trigger.scopeEveryoneExcept', { count: scope.contactCount });
                    const warning = scope.likelyInverted
                        ? t('chatbot.trigger.scopeInvertedWarning')
                        : scope.respondsToAllDirectMessages
                            ? t('chatbot.trigger.scopeAllDmWarning')
                            : '';
                    return (
                        <Box sx={{ mb: 2 }}>
                            <Box sx={{ p: 1.5, borderRadius: '12px', backgroundColor: 'var(--color-surface-2, rgba(0,0,0,0.04))' }}>
                                <Typography variant="caption" sx={{ display: 'block', color: 'var(--color-text-muted)', fontWeight: 600, mb: 0.25 }}>
                                    {t('chatbot.trigger.scopeSummaryLabel')}
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'var(--color-text)' }}>
                                    {summary}
                                </Typography>
                            </Box>
                            {warning && (
                                <Box sx={{ mt: 1, p: 1.5, borderRadius: '12px', backgroundColor: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.4)' }}>
                                    <Typography variant="body2" sx={{ color: 'var(--color-text)' }}>
                                        ⚠️ {warning}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    );
                })()}

                <RowStack
                    sx={{
                        gap: 1,
                        justifyContent: 'flex-end',
                        pt: 2,
                        mt: 2,
                        borderTop: '1px solid var(--color-border)',
                    }}
                >
                    <BTbotbutton
                        styleType="empty"
                        onClick={onClose}
                        size="medium"
                    >
                        {t('common.cancel')}
                    </BTbotbutton>
                    <SaveButton
                        onBeforeSave={saveTriggerData}
                        onAfterSave={onClose}
                        shouldClose={true}
                        disableBigLoader={true} />
                </RowStack>
            </Stack>
        </>
    );
} 
